import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterLink, RouterModule } from '@angular/router';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { LeafletMarkerClusterModule } from '@bluehalo/ngx-leaflet-markercluster';
import { LocalizeRouterModule } from '@gilsdav/ngx-translate-router';
import { TranslateModule } from '@ngx-translate/core';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { DrawerModule } from 'primeng/drawer';
import { FileUploadModule } from 'primeng/fileupload';
import { ImageModule } from 'primeng/image';
import { InplaceModule } from 'primeng/inplace';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { OverlayModule } from 'primeng/overlay';
import { PaginatorModule } from 'primeng/paginator';
import { PanelModule } from 'primeng/panel';
import { PanelMenuModule } from 'primeng/panelmenu';
import { ProgressBarModule } from 'primeng/progressbar';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { SplitButtonModule } from 'primeng/splitbutton';
import { StepperModule } from 'primeng/stepper';
import { StepsModule } from 'primeng/steps';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { TimelineModule } from 'primeng/timeline';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { TreeSelectModule } from 'primeng/treeselect';

import { CardviewGridComponent } from './cardview-grid/cardview-grid.component';
import { CardView } from './cardview-grid/cardview-grid.types';
import { InfoBarItemComponent } from './cardview-grid/info-bar-item.component';
import { CollapsibleMenuComponent } from './detail/collapsible-menu/collapsible-menu.component';
import { DetailActionButtonsComponent } from './detail/detail-action-button/detail-action-buttons.component';
import { DetailDialogComponent } from './detail/detail-dialog/detail-dialog.component';
import { DetailHeaderInfoComponent } from './detail/detail-header/detail-header-info.component';
import { DetailHeaderComponent } from './detail/detail-header/detail-header.component';
import { DetailOverlayComponent } from './detail/detail-overlay/detail-overlay.component';
import { DetailPageComponent } from './detail/detail-page/detail-page.component';
import { DetailSidebarComponent } from './detail/detail-sidebar/detail-sidebar.component';
import { DetailActionButtonToggleDialogComponent } from './detail/toggle/detail-action-button-toggle-dialog/detail-action-button-toggle-dialog.component';
import { DetailActionButtonToggleComponent } from './detail/toggle/detail-action-button-toggle/detail-action-button-toggle.component';
import { AnnouncementsDialogComponent } from './dialogs/announcements-dialog/announcements-dialog.component';
import { ConfirmationDialogComponent } from './dialogs/confirmation-dialog/confirmation-dialog.component';
import { MeetingpointersDialogComponent } from './dialogs/meetingpointers-dialog/meetingpointers-dialog.component';
import { DocumentComponent } from './document/document.component';
import { EmptyStateComponent } from './empty-state/empty-state.component';
import { ErrorStateComponent } from './error-state/error-state.component';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { FooterComponent } from './footer/footer.component';
import { FroalaEditorComponent } from './froala/froala-editor/froala-editor.component';
import { RichTextViewerComponent } from './froala/richtext/richtext-viewer.component';
import { InplaceComponent } from './inplace/inplace.component';
import { FavouritesComponent } from './navbar/favourites/favourites.component';
import { LiveSearchComponent } from './navbar/live-search/live-search.component';
import { NavbarComponent } from './navbar/navbar.component';
import { TieredMenuButtonComponent } from './navbar/tiered-menu-button/tiered-menu-button.component';
import { PanelMenuComponent } from './panel-menu/panel-menu.component';
import { PublicationComponent } from './publication/publication.component';
import { ResponsiveTablistComponent } from './responsive-tablist/responsive-tablist.component';
import { ResultsGraphComponent } from './results-graph/results-graph.component';
import { MapPopupComponent } from './results-map/map-popup/map-popup.component';
import { ResultsMapComponent } from './results-map/results-map.component';
import { ResultsTableComponent } from './results-table/results-table.component';
import { SpinnerTextComponent } from './spinner-text/spinner-text.component';
import { SplitbuttonComponent } from './splitbutton/splitbutton.component';
import { PaginatorOptions, TablePaginatorComponent } from './table-paginator/table-paginator.component';
import { ToastComponent } from './toast/toast.component';
import { TreeSelectComponent } from './treeSelect/tree-select/tree-select.component';
import { WalkthroughComponent } from './walkthrough/walkthrough.component';

import { SearchFacade } from '~/search/facades/search.facade';
import { SharedModule } from '~/shared/shared.module';

@NgModule({
  imports: [
    CommonModule,
    RouterModule,

    SkeletonModule,
    RouterLink,
    TieredMenuModule,
    LocalizeRouterModule,
    TranslateModule,
    CheckboxModule,
    ChipModule,
    DialogModule,
    TagModule,
    FormsModule,
    ReactiveFormsModule,
    BadgeModule,
    ButtonModule,
    DrawerModule,
    PanelMenuModule,
    SplitButtonModule,
    OverlayModule,
    InputTextModule,
    AutoCompleteModule,
    MenuModule,
    PaginatorModule,
    TableModule,
    TreeSelectModule,
    InplaceModule,
    TextareaModule,
    StepsModule,
    StepperModule,
    ImageModule,
    FileUploadModule,
    ProgressBarModule,
    RadioButtonModule,
    TooltipModule,
    PanelModule,
    DividerModule,
    ToastModule,
    SelectModule,
    TabsModule,

    FroalaEditorModule.forRoot(),
    FroalaViewModule.forRoot(),

    SharedModule,
    TimelineModule,
    CardModule,
    ButtonGroupModule,

    LeafletModule,
    LeafletMarkerClusterModule
  ],
  declarations: [
    NavbarComponent,
    InfoBarItemComponent,
    EmptyStateComponent,
    TieredMenuButtonComponent,
    FavouritesComponent,
    FooterComponent,
    LiveSearchComponent,
    AnnouncementsDialogComponent,
    MeetingpointersDialogComponent,
    ResultsGraphComponent,
    ResultsTableComponent,
    TreeSelectComponent,
    TablePaginatorComponent,
    DetailHeaderComponent,
    DetailHeaderInfoComponent,
    DocumentComponent,
    PublicationComponent,
    FroalaEditorComponent,
    FroalaEditorComponent,
    RichTextViewerComponent,
    InplaceComponent,
    WalkthroughComponent,
    SplitbuttonComponent,
    FileUploadComponent,
    ConfirmationDialogComponent,
    CardviewGridComponent,
    DetailSidebarComponent,
    ResultsMapComponent,
    MapPopupComponent,
    CollapsibleMenuComponent,
    DetailOverlayComponent,
    DetailDialogComponent,
    PanelMenuComponent,
    ErrorStateComponent,
    DetailActionButtonsComponent,
    ToastComponent,
    DetailPageComponent,
    ResponsiveTablistComponent,
    SpinnerTextComponent,
    DetailPageComponent,
    DetailActionButtonToggleComponent,
    DetailActionButtonToggleDialogComponent
  ],
  exports: [
    NavbarComponent,
    DocumentComponent,
    PublicationComponent,
    InfoBarItemComponent,
    EmptyStateComponent,
    FooterComponent,
    AnnouncementsDialogComponent,
    MeetingpointersDialogComponent,
    LiveSearchComponent,
    MeetingpointersDialogComponent,
    ResultsGraphComponent,
    ResultsTableComponent,
    TieredMenuButtonComponent,
    TreeSelectComponent,
    TablePaginatorComponent,
    DetailHeaderComponent,
    DetailSidebarComponent,
    DetailPageComponent,
    WalkthroughComponent,
    SplitbuttonComponent,
    FileUploadComponent,
    ConfirmationDialogComponent,
    ResultsMapComponent,
    CardviewGridComponent,
    CollapsibleMenuComponent,
    DetailOverlayComponent,
    DetailDialogComponent,
    PanelMenuComponent,
    ErrorStateComponent,
    DetailActionButtonsComponent,
    ToastComponent,
    InplaceComponent,
    FroalaEditorComponent,
    FroalaEditorComponent,
    RichTextViewerComponent,
    DetailPageComponent,
    ResponsiveTablistComponent,
    SpinnerTextComponent
  ],
  providers: [SearchFacade]
})
export class UiModule {}

export { CardView, PaginatorOptions };
